// src/api/patient.js

import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Add request interceptor to include auth token in headers
API.interceptors.request.use(
  (config) => {
    const authData = JSON.parse(localStorage.getItem("authData") || "null");
    if (authData?.accessToken) {
      config.headers.Authorization = `Bearer ${authData.accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ===== Consultation APIs (visits-aware) =====

/**
 * Add a new consultation visit to an appointment
 * @param {Object} data - { appointmentId, date, time, doctorId?, observations?, complaints?, diagnosis?, advice?, treatments?, prescribedMedicines?, followUpDate?, notes? }
 * @returns {Promise} Resolves to { message, consultation, visit }
 */
export const addConsultationVisit = (data) =>
  API.post("/add-consultation", data);

/**
 * Edit an existing consultation visit
 * @param {Object} data - { appointmentId, visitId, date?, time?, ...other fields to update }
 * @returns {Promise} Resolves to { message, consultation, visit }
 */
export const editConsultationVisit = (data) =>
  API.put("/edit-consultation", data);

/**
 * Get all consultations (visits) for an appointment
 * @param {string} appointmentId - MongoDB appointment ID
 * @returns {Promise} Resolves to { consultation: { _id, appointment, doctor, invoiceId, visits[], visitCount } }
 */
export const getConsultationByAppointment = (appointmentId) =>
  API.get(`/consultations/${appointmentId}`);

/**
 * Get patient history with all appointments and their visits
 * @param {string} patientId - MongoDB patient ID
 * @returns {Promise} Resolves to { patientId, history: [ { appointmentId, appointmentDate, doctor, status, consultation: { visits[], visitCount }, visits[], visitCount } ] }
 */
export const getPatientHistory = (patientId) =>
  API.get(`/patient-history?patientId=${patientId}`);

// ===== Patient APIs (branch and campaign fields) =====

/**
 * Register a new patient
 * @param {Object} data - { name, email?, phone, age?, gender?, branch?, is_campaign_referred?, campaign_name?, ...other fields }
 * @returns {Promise}
 */
export const registerPatient = (data) =>
  API.post("/register-patient", data);

/**
 * Update patient branch
 * @param {string} patientId - MongoDB patient ID
 * @param {string} branch - Branch name (string, not ObjectId)
 * @returns {Promise}
 */
export const updatePatientBranch = (patientId, branch) =>
  API.put("/edit-branch", { patientId, branch });

/**
 * Get patient by ID
 * Uses the same endpoint as PatientInfo / BillingInfo for consistent payload shape.
 * @param {string} patientId - MongoDB patient ID
 * @returns {Promise} Resolves to { result: patient }
 */
export const getPatientById = (patientId) =>
  API.get(`/get-patient`, { params: { patientId } });

/**
 * Get all patients (with optional filters)
 * @param {Object} filters - { branch?, page?, pageSize?, search? }
 * @returns {Promise}
 */
export const getAllPatients = (filters = {}) =>
  API.get("/patients", { params: filters });

/**
 * Search patients
 * @param {string} query - Search query (name, phone, email)
 * @param {Object} params - { branch?, page?, pageSize? }
 * @returns {Promise}
 */
export const searchPatients = (query, params = {}) =>
  API.get("/search-patient", { params: { ...params, query } });

/**
 * Update patient
 * @param {string} patientId - MongoDB patient ID
 * @param {Object} data - Fields to update
 * @returns {Promise}
 */
export const updatePatient = (patientId, data) =>
  API.put(`/edit-patient/${patientId}`, data);
